import * as React from "react";
import styled, {
  css,
  ThemeContext
} from "styled-components";

import Grid from "src/library/components/Hedron";
import { InstalledModuleType } from "src/utils/constants";
import BrandedImage from "src/core/shared/components/BrandedImage";
import { IProduct } from "src/core/products/api/products.api";
import { IRole } from "src/core/shared/api/roles.api";
import { IPromotionalTag } from "src/modules/promotions/api/promotional-tags.api";
import PromotionalTagLabel from "src/modules/promotions/components/PromotionalTagLabel";

import { ProductDetails } from "../ProductDetails";
import { useQuickViewProduct } from "../QuickViewProduct";

export interface IStandardProductCardProps {
  productImage?: string;
  product: IProduct;
  ProductDetails?: React.ReactNode;
  className?: string;
  currentRole: IRole | null;
  installedModules: InstalledModuleType[];
  unavailable?: string | null;
  featured?: boolean;
  onCardActive?: (isActive: boolean) => void;
  originalProduct?: IProduct;
}

export const StandardProductCard = React.memo<IStandardProductCardProps>(function StandardProductCard({
  product,
  productImage,
  className,
  unavailable,
  featured,
  installedModules,
  onCardActive,
  originalProduct
}) {
  const theme = React.useContext(ThemeContext);
  const [ orderModalOpen, setOrderModalOpen ] = React.useState(false);

  // if applicable set promotional tag data
  const promotionalTagData: IPromotionalTag | null = React.useMemo(() => {
    if (installedModules.includes("promotion") && product.promotionalTags && product.promotionalTags.length) {
      return product.promotionalTags[ 0 ];
    }

    return null;
  }, [ installedModules, product.promotionalTags ]);

  const { setQuickViewProduct } = useQuickViewProduct();

  const onOrderModeChange = React.useCallback((isOpen: boolean) => {
    if (isOpen !== orderModalOpen && onCardActive) {
      onCardActive(isOpen);
    }

    setOrderModalOpen(isOpen);
  }, [ onCardActive, orderModalOpen ]);

  return (
    <ProductCardWrapper
      direction="vertical"
      className={`${className} ${orderModalOpen ? "active" : ""}`}
      featured={featured}
    >
      <ProductImageWrapper>
        {promotionalTagData && (
          <PromotionalTagLabel
            color={promotionalTagData.color}
            text={promotionalTagData.label}
          />
        )}

        <div
          onClick={() => setQuickViewProduct(product.id)}
          className={`product-image ${productImage ? "hasImage" : "placeholder"}`}
        >
          <BrandedImage
            s3Key={productImage || theme.customDarkLogo}
            alt={product.description}
            level="public"
          />
        </div>
      </ProductImageWrapper>

      <ProductDetails
        originalProduct={originalProduct}
        onOrderModalChange={onOrderModeChange}
        className="product-details"
        product={product}
        unavailable={unavailable}
        orderModalPosition={featured ? "overlay" : "left"}
      />
    </ProductCardWrapper>
  );
});

export const ProductCardWrapper = styled(Grid.Bounds)< { featured: boolean; } >`
${({ featured }) => css`
    max-width: 400px;
    position: relative;
    height: 100%;

    &.disabled {
      opacity: 0.3;
    }

    ${featured ? css`
      z-index: 1;
      border-radius: 10px;

      .placeholder, .hasImage {
        border-radius: 10px 10px 0px 0px;
      }

      .product-details {
        border-radius: 0px 0px 10px 10px;


        .button, .product-card-tags, .part-number-wrapper, .part-number, .add-product-wrapper, .product-detail-configurable-item {
          visibility: hidden;
          position: absolute;
          transform: scaleY(0);
          opacity: 0;
          transition: none;
        }
      }
      
      transition: all ease-out 0.25s;
      height: 273px;
      width: 210px;
      
      
      &:not(.disabled):hover, &.active {
        box-shadow: 0 0 25px -5px rgb(0 0 0 / 60%);
        transform: scale(1.15);
        width: 230px;

        .button, .product-card-tags, .part-number-wrapper, .part-number, .add-product-wrapper, .product-detail-configurable-item {
          visibility: visible;
          transform: scaleY(1);
          opacity: 1;
          position: relative;
        }

        .add-product-wrapper {
          transform: scale(0.8);
        }
      }
    ` : ""
}
  `}
`;

export const ProductImageWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    .product-image {
      background: ${theme.colors.white};
      box-sizing: border-box;
      cursor: pointer;

      &.hasImage {
        background: white;
      }
      position: relative;
      width: 100%;
      padding-bottom: 67%;
      overflow: hidden;

      .product-card-tags {
        z-index: 1;
        position: absolute;
        top: 0;
        left: ${theme.spacing.margins.sm};
      }

    &.placeholder {
      img {
        /* width: 50px; */
      }
    }

    img {
      height: auto;
      width: auto;
      transform: translate(-50%, -50%);
      top: 50%;
      left: 50%;
      max-width: 100%;
      max-height: 100%;
      position: absolute;
      &.placeholder {
        width: 30px;
      }
    }
  `}
`;
